import React from 'react';
import { observer } from 'mobx-react-lite';
import { useDroppable } from '@dnd-kit/core';
import { Table } from '@tanstack/react-table';

import { ShotStrip } from './shot-strip';
import { UnscheduledHeader } from './unscheduled-header';
import { BannerStrip } from './banner-strip';
import { context } from '../../../app/context';
import Styles from '../stripboard.module.scss';

import { Step, type Stripboard } from '../../../app/entities';
import type { Strip } from '../../../app/entities/strip';

type UnscheduledShotsTableProps = {
  table: Table<Strip>;
  totalSize: number | string;
  projectId: string;
  onAddShot?: () => void;
  stripboard: Stripboard;
  onUpdateStrip?: (
    stripId: string,
    update: {
      data?: {
        text?: string;
        estimatedTime?: number;
        isHidden?: boolean;
      };
    },
  ) => Promise<void>;
};

export const UnscheduledShotsTable: React.FC<UnscheduledShotsTableProps> = observer(
  ({ table, totalSize, projectId, stripboard, onAddShot, onUpdateStrip }) => {
    const stepId = context.step?._id;
    const step = Step.getOne(stepId);
    const view = step?.resource;

    const { setNodeRef, isOver, active } = useDroppable({
      id: 'unscheduled-strips',
      data: {
        type: 'unscheduled-table',
        accepts: ['row'],
      },
    });

    const rows = table.getRowModel().rows;

    return (
      <div
        style={{
          width: `max(${totalSize}px, 100%)`,
        }}
        className={Styles.singleTable}
        ref={setNodeRef}
      >
        <UnscheduledHeader
          title={view?.options?.showBanner ? 'Unscheduled shots and banners' : 'Unscheduled shots'}
        />

        <div className={Styles['table-body']}>
          {rows?.length === 0 && (
            <div className={Styles.stickyStrip} data-variant="empty-placeholder">
              {active?.data?.current?.type === 'row' && isOver && (
                <div className={Styles.stickyStripContent}>
                  <div className={Styles['drop-target']} />
                </div>
              )}
            </div>
          )}

          {rows?.map((row) => {
            const strip = row.original;

            if (strip?.type === 'banner') {
              const handleUpdate = async (text?: string) => {
                onUpdateStrip?.(strip._id, { data: { text } });
              };

              return (
                <BannerStrip
                  key={strip?._id}
                  row={row}
                  projectId={projectId}
                  onUpdateText={handleUpdate}
                />
              );
            }

            return <ShotStrip key={strip?._id} row={row} stripboard={stripboard} />;
          })}

          <div className={Styles.stickyStrip} data-variant="add-shot">
            <div
              className={`${Styles.stickyStripContent} ${Styles.addShotStrip}`}
              onClick={onAddShot}
            >
              Add shot
            </div>
          </div>
        </div>
      </div>
    );
  },
);
